export interface ThemeColor {
  key: string;
  label: string;
  description: string;
  defaultValue: string; // HSL format: "h s% l%"
  category: "brand" | "base" | "ui" | "status" | "layout";
}

// Dark theme defaults (current production values)
export const THEME_COLORS_DARK: ThemeColor[] = [
  // Brand Colors
  {
    key: "theme_color_primary",
    label: "Primary Color",
    description: "Main brand color used for buttons, links, and highlights",
    defaultValue: "256 82% 75%",
    category: "brand",
  },
  {
    key: "theme_color_primary_light",
    label: "Primary Light",
    description: "Lighter variant of primary color",
    defaultValue: "256 100% 94%",
    category: "brand",
  },
  {
    key: "theme_color_primary_dark",
    label: "Primary Dark",
    description: "Darker variant of primary color",
    defaultValue: "268 29% 51%",
    category: "brand",
  },
  {
    key: "theme_color_primary_foreground",
    label: "Primary Foreground",
    description: "Text color on primary background",
    defaultValue: "0 0% 100%",
    category: "brand",
  },

  // Base Colors
  {
    key: "theme_color_background",
    label: "Background",
    description: "Main background color",
    defaultValue: "229 25% 8%",
    category: "base",
  },
  {
    key: "theme_color_foreground",
    label: "Foreground/Text",
    description: "Main text color",
    defaultValue: "215 25% 91%",
    category: "base",
  },
  {
    key: "theme_color_border",
    label: "Border",
    description: "Border color for elements",
    defaultValue: "228 22% 21%",
    category: "base",
  },

  // UI Colors
  {
    key: "theme_color_secondary",
    label: "Secondary",
    description: "Secondary UI surface color",
    defaultValue: "228 22% 21%",
    category: "ui",
  },
  {
    key: "theme_color_secondary_foreground",
    label: "Secondary Foreground",
    description: "Text color on secondary background",
    defaultValue: "215 25% 91%",
    category: "ui",
  },
  {
    key: "theme_color_accent",
    label: "Accent",
    description: "Accent color for subtle highlights",
    defaultValue: "225 18% 15%",
    category: "ui",
  },
  {
    key: "theme_color_accent_foreground",
    label: "Accent Foreground",
    description: "Text color on accent background",
    defaultValue: "215 25% 91%",
    category: "ui",
  },
  {
    key: "theme_color_muted",
    label: "Muted",
    description: "Muted background color",
    defaultValue: "234 24% 29%",
    category: "ui",
  },
  {
    key: "theme_color_muted_foreground",
    label: "Muted Foreground",
    description: "Muted text color",
    defaultValue: "240 5% 65%",
    category: "ui",
  },
  {
    key: "theme_color_card",
    label: "Card Background",
    description: "Card background color",
    defaultValue: "225 18% 15%",
    category: "ui",
  },
  {
    key: "theme_color_card_foreground",
    label: "Card Foreground",
    description: "Text color on card background",
    defaultValue: "215 25% 91%",
    category: "ui",
  },

  // Status Colors
  {
    key: "theme_color_success",
    label: "Success",
    description: "Success message color",
    defaultValue: "142 71% 45%",
    category: "status",
  },
  {
    key: "theme_color_success_foreground",
    label: "Success Foreground",
    description: "Text color on success background",
    defaultValue: "0 0% 100%",
    category: "status",
  },
  {
    key: "theme_color_destructive",
    label: "Destructive/Error",
    description: "Error and destructive action color",
    defaultValue: "0 100% 67%",
    category: "status",
  },
  {
    key: "theme_color_destructive_foreground",
    label: "Destructive Foreground",
    description: "Text color on destructive background",
    defaultValue: "0 0% 100%",
    category: "status",
  },
  {
    key: "theme_color_warning",
    label: "Warning",
    description: "Warning message color (yellow/amber)",
    defaultValue: "45 93% 47%",
    category: "status",
  },
  {
    key: "theme_color_warning_foreground",
    label: "Warning Foreground",
    description: "Text color on warning background",
    defaultValue: "0 0% 0%",
    category: "status",
  },
  {
    key: "theme_color_info",
    label: "Info",
    description: "Informational message color (blue)",
    defaultValue: "200 98% 39%",
    category: "status",
  },
  {
    key: "theme_color_info_foreground",
    label: "Info Foreground",
    description: "Text color on info background",
    defaultValue: "0 0% 100%",
    category: "status",
  },

  // Layout Colors
  {
    key: "theme_color_navbar_background",
    label: "Navbar Background",
    description: "Navigation bar background color",
    defaultValue: "0 0% 0%",
    category: "layout",
  },
  {
    key: "theme_color_navbar_foreground",
    label: "Navbar Text",
    description: "Navigation bar text color",
    defaultValue: "0 0% 100%",
    category: "layout",
  },
  {
    key: "theme_color_navbar_border",
    label: "Navbar Border",
    description: "Navigation bar border color",
    defaultValue: "0 0% 100%",
    category: "layout",
  },
  {
    key: "theme_color_footer_background",
    label: "Footer Background",
    description: "Footer background color",
    defaultValue: "0 0% 0%",
    category: "layout",
  },
  {
    key: "theme_color_footer_foreground",
    label: "Footer Text",
    description: "Footer heading text color",
    defaultValue: "0 0% 100%",
    category: "layout",
  },
  {
    key: "theme_color_footer_muted",
    label: "Footer Secondary Text",
    description: "Footer body and link text color",
    defaultValue: "0 0% 64%",
    category: "layout",
  },
];

// Light theme defaults (from CSS .light class)
export const THEME_COLORS_LIGHT: ThemeColor[] = [
  // Brand Colors
  {
    key: "theme_color_primary_light_mode",
    label: "Primary Color",
    description: "Main brand color used for buttons, links, and highlights",
    defaultValue: "256 82% 65%",
    category: "brand",
  },
  {
    key: "theme_color_primary_light_light_mode",
    label: "Primary Light",
    description: "Lighter variant of primary color",
    defaultValue: "256 100% 90%",
    category: "brand",
  },
  {
    key: "theme_color_primary_dark_light_mode",
    label: "Primary Dark",
    description: "Darker variant of primary color",
    defaultValue: "268 29% 41%",
    category: "brand",
  },
  {
    key: "theme_color_primary_foreground_light_mode",
    label: "Primary Foreground",
    description: "Text color on primary background",
    defaultValue: "0 0% 100%",
    category: "brand",
  },

  // Base Colors
  {
    key: "theme_color_background_light_mode",
    label: "Background",
    description: "Main background color",
    defaultValue: "0 0% 100%",
    category: "base",
  },
  {
    key: "theme_color_foreground_light_mode",
    label: "Foreground/Text",
    description: "Main text color",
    defaultValue: "222 47% 11%",
    category: "base",
  },
  {
    key: "theme_color_border_light_mode",
    label: "Border",
    description: "Border color for elements",
    defaultValue: "220 13% 91%",
    category: "base",
  },

  // UI Colors
  {
    key: "theme_color_secondary_light_mode",
    label: "Secondary",
    description: "Secondary UI surface color",
    defaultValue: "220 14% 96%",
    category: "ui",
  },
  {
    key: "theme_color_secondary_foreground_light_mode",
    label: "Secondary Foreground",
    description: "Text color on secondary background",
    defaultValue: "222 47% 11%",
    category: "ui",
  },
  {
    key: "theme_color_accent_light_mode",
    label: "Accent",
    description: "Accent color for subtle highlights",
    defaultValue: "220 14% 96%",
    category: "ui",
  },
  {
    key: "theme_color_accent_foreground_light_mode",
    label: "Accent Foreground",
    description: "Text color on accent background",
    defaultValue: "222 47% 11%",
    category: "ui",
  },
  {
    key: "theme_color_muted_light_mode",
    label: "Muted",
    description: "Muted background color",
    defaultValue: "220 14% 96%",
    category: "ui",
  },
  {
    key: "theme_color_muted_foreground_light_mode",
    label: "Muted Foreground",
    description: "Muted text color",
    defaultValue: "220 9% 46%",
    category: "ui",
  },
  {
    key: "theme_color_card_light_mode",
    label: "Card Background",
    description: "Card background color",
    defaultValue: "0 0% 100%",
    category: "ui",
  },
  {
    key: "theme_color_card_foreground_light_mode",
    label: "Card Foreground",
    description: "Text color on card background",
    defaultValue: "222 47% 11%",
    category: "ui",
  },

  // Status Colors
  {
    key: "theme_color_success_light_mode",
    label: "Success",
    description: "Success message color",
    defaultValue: "142 71% 45%",
    category: "status",
  },
  {
    key: "theme_color_success_foreground_light_mode",
    label: "Success Foreground",
    description: "Text color on success background",
    defaultValue: "0 0% 100%",
    category: "status",
  },
  {
    key: "theme_color_destructive_light_mode",
    label: "Destructive/Error",
    description: "Error and destructive action color",
    defaultValue: "0 84% 60%",
    category: "status",
  },
  {
    key: "theme_color_destructive_foreground_light_mode",
    label: "Destructive Foreground",
    description: "Text color on destructive background",
    defaultValue: "0 0% 100%",
    category: "status",
  },
  {
    key: "theme_color_warning_light_mode",
    label: "Warning",
    description: "Warning message color (yellow/amber)",
    defaultValue: "38 92% 50%",
    category: "status",
  },
  {
    key: "theme_color_warning_foreground_light_mode",
    label: "Warning Foreground",
    description: "Text color on warning background",
    defaultValue: "0 0% 0%",
    category: "status",
  },
  {
    key: "theme_color_info_light_mode",
    label: "Info",
    description: "Informational message color (blue)",
    defaultValue: "200 98% 39%",
    category: "status",
  },
  {
    key: "theme_color_info_foreground_light_mode",
    label: "Info Foreground",
    description: "Text color on info background",
    defaultValue: "0 0% 100%",
    category: "status",
  },

  // Layout Colors
  {
    key: "theme_color_navbar_background_light_mode",
    label: "Navbar Background",
    description: "Navigation bar background color",
    defaultValue: "0 0% 100%",
    category: "layout",
  },
  {
    key: "theme_color_navbar_foreground_light_mode",
    label: "Navbar Text",
    description: "Navigation bar text color",
    defaultValue: "222 47% 11%",
    category: "layout",
  },
  {
    key: "theme_color_navbar_border_light_mode",
    label: "Navbar Border",
    description: "Navigation bar border color",
    defaultValue: "220 13% 91%",
    category: "layout",
  },
  {
    key: "theme_color_footer_background_light_mode",
    label: "Footer Background",
    description: "Footer background color",
    defaultValue: "220 14% 96%",
    category: "layout",
  },
  {
    key: "theme_color_footer_foreground_light_mode",
    label: "Footer Text",
    description: "Footer heading text color",
    defaultValue: "222 47% 11%",
    category: "layout",
  },
  {
    key: "theme_color_footer_muted_light_mode",
    label: "Footer Secondary Text",
    description: "Footer body and link text color",
    defaultValue: "220 9% 46%",
    category: "layout",
  },
];

// Custom theme defaults (starts with dark theme values - admin will customize)
export const THEME_COLORS_CUSTOM: ThemeColor[] = [
  // Brand Colors
  {
    key: "theme_color_primary_custom",
    label: "Primary Color",
    description: "Main brand color used for buttons, links, and highlights",
    defaultValue: "256 82% 75%",
    category: "brand",
  },
  {
    key: "theme_color_primary_light_custom",
    label: "Primary Light",
    description: "Lighter variant of primary color",
    defaultValue: "256 100% 94%",
    category: "brand",
  },
  {
    key: "theme_color_primary_dark_custom",
    label: "Primary Dark",
    description: "Darker variant of primary color",
    defaultValue: "268 29% 51%",
    category: "brand",
  },
  {
    key: "theme_color_primary_foreground_custom",
    label: "Primary Foreground",
    description: "Text color on primary background",
    defaultValue: "0 0% 100%",
    category: "brand",
  },

  // Base Colors
  {
    key: "theme_color_background_custom",
    label: "Background",
    description: "Main background color",
    defaultValue: "229 25% 8%",
    category: "base",
  },
  {
    key: "theme_color_foreground_custom",
    label: "Foreground/Text",
    description: "Main text color",
    defaultValue: "215 25% 91%",
    category: "base",
  },
  {
    key: "theme_color_border_custom",
    label: "Border",
    description: "Border color for elements",
    defaultValue: "228 22% 21%",
    category: "base",
  },

  // UI Colors
  {
    key: "theme_color_secondary_custom",
    label: "Secondary",
    description: "Secondary UI surface color",
    defaultValue: "228 22% 21%",
    category: "ui",
  },
  {
    key: "theme_color_secondary_foreground_custom",
    label: "Secondary Foreground",
    description: "Text color on secondary background",
    defaultValue: "215 25% 91%",
    category: "ui",
  },
  {
    key: "theme_color_accent_custom",
    label: "Accent",
    description: "Accent color for subtle highlights",
    defaultValue: "225 18% 15%",
    category: "ui",
  },
  {
    key: "theme_color_accent_foreground_custom",
    label: "Accent Foreground",
    description: "Text color on accent background",
    defaultValue: "215 25% 91%",
    category: "ui",
  },
  {
    key: "theme_color_muted_custom",
    label: "Muted",
    description: "Muted background color",
    defaultValue: "234 24% 29%",
    category: "ui",
  },
  {
    key: "theme_color_muted_foreground_custom",
    label: "Muted Foreground",
    description: "Muted text color",
    defaultValue: "240 5% 65%",
    category: "ui",
  },
  {
    key: "theme_color_card_custom",
    label: "Card Background",
    description: "Card background color",
    defaultValue: "225 18% 15%",
    category: "ui",
  },
  {
    key: "theme_color_card_foreground_custom",
    label: "Card Foreground",
    description: "Text color on card background",
    defaultValue: "215 25% 91%",
    category: "ui",
  },

  // Status Colors
  {
    key: "theme_color_success_custom",
    label: "Success",
    description: "Success message color",
    defaultValue: "142 71% 45%",
    category: "status",
  },
  {
    key: "theme_color_success_foreground_custom",
    label: "Success Foreground",
    description: "Text color on success background",
    defaultValue: "0 0% 100%",
    category: "status",
  },
  {
    key: "theme_color_destructive_custom",
    label: "Destructive/Error",
    description: "Error and destructive action color",
    defaultValue: "0 100% 67%",
    category: "status",
  },
  {
    key: "theme_color_destructive_foreground_custom",
    label: "Destructive Foreground",
    description: "Text color on destructive background",
    defaultValue: "0 0% 100%",
    category: "status",
  },
  {
    key: "theme_color_warning_custom",
    label: "Warning",
    description: "Warning message color (yellow/amber)",
    defaultValue: "45 93% 47%",
    category: "status",
  },
  {
    key: "theme_color_warning_foreground_custom",
    label: "Warning Foreground",
    description: "Text color on warning background",
    defaultValue: "0 0% 0%",
    category: "status",
  },
  {
    key: "theme_color_info_custom",
    label: "Info",
    description: "Informational message color (blue)",
    defaultValue: "200 98% 39%",
    category: "status",
  },
  {
    key: "theme_color_info_foreground_custom",
    label: "Info Foreground",
    description: "Text color on info background",
    defaultValue: "0 0% 100%",
    category: "status",
  },

  // Layout Colors
  {
    key: "theme_color_navbar_background_custom",
    label: "Navbar Background",
    description: "Navigation bar background color",
    defaultValue: "0 0% 0%",
    category: "layout",
  },
  {
    key: "theme_color_navbar_foreground_custom",
    label: "Navbar Text",
    description: "Navigation bar text color",
    defaultValue: "0 0% 100%",
    category: "layout",
  },
  {
    key: "theme_color_navbar_border_custom",
    label: "Navbar Border",
    description: "Navigation bar border color",
    defaultValue: "0 0% 100%",
    category: "layout",
  },
  {
    key: "theme_color_footer_background_custom",
    label: "Footer Background",
    description: "Footer background color",
    defaultValue: "0 0% 0%",
    category: "layout",
  },
  {
    key: "theme_color_footer_foreground_custom",
    label: "Footer Text",
    description: "Footer heading text color",
    defaultValue: "0 0% 100%",
    category: "layout",
  },
  {
    key: "theme_color_footer_muted_custom",
    label: "Footer Secondary Text",
    description: "Footer body and link text color",
    defaultValue: "0 0% 64%",
    category: "layout",
  },
];

// Legacy export for backward compatibility
export const THEME_COLORS = THEME_COLORS_DARK;

// Type for theme modes
export type ThemeMode = "dark" | "light" | "custom";

// Helper function to get colors for a specific mode
export function getThemeColorsForMode(mode: ThemeMode): ThemeColor[] {
  if (mode === "light") return THEME_COLORS_LIGHT;
  if (mode === "custom") return THEME_COLORS_CUSTOM;
  return THEME_COLORS_DARK;
}

// Helper to convert light mode or custom key to base key (for CSS variable mapping)
export function getBaseKey(key: string): string {
  return key.replace("_light_mode", "").replace("_custom", "");
}

export const THEME_CATEGORIES = {
  brand: "Brand Colors",
  base: "Base Colors",
  ui: "UI Colors",
  status: "Status Colors",
  layout: "Layout Colors",
} as const;
